<template>
  <div 
    class="tabs-scroller-container" 
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div 
      class="scroll-arrow left-arrow" 
      :class="{ hidden: !showLeftArrow || !isHovering }"
      @click="scrollTabs(-1)"
    >
      <i class="el-icon-arrow-left"></i>
    </div>
    
    <div class="tabs-container" ref="tabsContainer">
      <div
        v-for="(tab, index) in tabs"
        :key="index"
        class="tab-item"
        :class="{ active: currentTab === index }"
        @click="selectTab(index)"
        :ref="`tab${index}`"
      >
        {{ tab }}
        <div v-if="currentTab === index" class="tab-indicator"></div>
      </div>
    </div>
    
    <div 
      class="scroll-arrow right-arrow" 
      :class="{ hidden: !showRightArrow || !isHovering }"
      @click="scrollTabs(1)"
    >
      <i class="el-icon-arrow-right"></i>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TabsLable',
  props: {
    tabs: {
      type: Array,
      default: () => []
    },
    value: {
      type: Number,
      default: 0
    },
    visibleItems: {
      type: Number,
      default: 3
    },
    // 新增：箭头显示延迟时间（毫秒）
    arrowDelay: {
      type: Number,
      default: 100
    }
  },
  data() {
    return {
      currentTab: this.value,
      showLeftArrow: false,
      showRightArrow: false,
      isHovering: false,
      tabWidths: [],
      containerWidth: 0,
      hoverTimeout: null
    };
  },
  watch: {
    value(newVal) {
      this.currentTab = newVal;
      this.$nextTick(() => {
        this.ensureTabVisible(newVal, true);
      });
    },
    tabs() {
      this.$nextTick(() => {
        this.calculateTabWidths();
        this.checkArrowVisibility();
      });
    }
  },
  mounted() {
    this.calculateTabWidths();
    this.checkArrowVisibility();
    window.addEventListener('resize', this.handleResize);
    this.$refs.tabsContainer.addEventListener('scroll', this.checkArrowVisibility);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.$refs.tabsContainer) {
      this.$refs.tabsContainer.removeEventListener('scroll', this.checkArrowVisibility);
    }
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
  },
  methods: {
    // 鼠标进入区域
    handleMouseEnter() {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
      }
      this.hoverTimeout = setTimeout(() => {
        this.isHovering = true;
      }, this.arrowDelay);
    },
    
    // 鼠标离开区域
    handleMouseLeave() {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
      }
      this.isHovering = false;
    },
    
    // 计算每个标签的宽度
    calculateTabWidths() {
      this.$nextTick(() => {
        this.tabWidths = [];
        this.containerWidth = this.$refs.tabsContainer.clientWidth;
        
        for (let i = 0; i < this.tabs.length; i++) {
          const tabRef = this.$refs[`tab${i}`];
          if (tabRef && tabRef[0]) {
            this.tabWidths.push(tabRef[0].offsetWidth);
          } else {
            this.tabWidths.push(80);
          }
        }
      });
    },
    
    // 处理窗口大小变化
    handleResize() {
      this.calculateTabWidths();
      this.checkArrowVisibility();
    },
    
    // 检查箭头显示状态
    checkArrowVisibility() {
      if (!this.$refs.tabsContainer) return;
      
      const container = this.$refs.tabsContainer;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const scrollLeft = container.scrollLeft;
      
      this.showLeftArrow = scrollLeft > 0;
      this.showRightArrow = scrollLeft < scrollWidth - clientWidth - 1;
    },
    
    // 智能滚动标签
    scrollTabs(direction) {
      const container = this.$refs.tabsContainer;
      const currentScrollLeft = container.scrollLeft;
      
      let scrollAmount = 0;
      
      if (direction === 1) {
        const visibleEnd = currentScrollLeft + this.containerWidth;
        let accumulatedWidth = 0;
        
        for (let i = 0; i < this.tabs.length; i++) {
          accumulatedWidth += this.tabWidths[i];
          if (accumulatedWidth > visibleEnd) {
            const targetScrollLeft = accumulatedWidth - this.containerWidth;
            scrollAmount = targetScrollLeft - currentScrollLeft;
            break;
          }
        }
        
        if (scrollAmount <= 0) {
          scrollAmount = this.containerWidth * 0.7;
        }
      } else {
        let accumulatedWidth = 0;
        
        for (let i = 0; i < this.tabs.length; i++) {
          accumulatedWidth += this.tabWidths[i];
          if (accumulatedWidth >= currentScrollLeft) {
            const prevIndex = Math.max(0, i - this.visibleItems);
            let prevAccumulatedWidth = 0;
            
            for (let j = 0; j < prevIndex; j++) {
              prevAccumulatedWidth += this.tabWidths[j];
            }
            
            scrollAmount = prevAccumulatedWidth - currentScrollLeft;
            break;
          }
        }
        
        if (scrollAmount >= 0) {
          scrollAmount = -this.containerWidth * 0.7;
        }
      }
      
      container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
      
      setTimeout(() => {
        this.checkArrowVisibility();
      }, 300);
    },
    
    // 选择标签
    selectTab(index) {
      this.currentTab = index;
      this.$emit('input', index);
      this.$emit('change', index,this.tabs[index]);
      this.ensureTabVisible(index, true);
    },
    
    // 确保标签在可视区域内
    ensureTabVisible(index, smartScroll = false) {
      this.$nextTick(() => {
        const container = this.$refs.tabsContainer;
        const tab = this.$refs[`tab${index}`] && this.$refs[`tab${index}`][0];
        
        if (tab) {
          const tabLeft = tab.offsetLeft;
          const tabWidth = tab.offsetWidth;
          const containerWidth = container.clientWidth;
          const scrollLeft = container.scrollLeft;
          
          if (smartScroll) {
            const tabPositionPercent = (tabLeft - scrollLeft) / containerWidth;
            
            if (tabLeft < scrollLeft) {
              const scrollTarget = Math.max(0, tabLeft - (containerWidth * 0.2));
              container.scrollTo({
                left: scrollTarget,
                behavior: 'smooth'
              });
            } else if (tabLeft + tabWidth > scrollLeft + containerWidth) {
              const maxScroll = container.scrollWidth - containerWidth;
              const scrollTarget = Math.min(maxScroll, tabLeft - (containerWidth * 0.8) + tabWidth);
              container.scrollTo({
                left: scrollTarget,
                behavior: 'smooth'
              });
            } else if (tabPositionPercent < 0.2) {
              const scrollTarget = Math.max(0, scrollLeft - (containerWidth * 0.3));
              container.scrollTo({
                left: scrollTarget,
                behavior: 'smooth'
              });
            } else if (tabPositionPercent > 0.8) {
              const maxScroll = container.scrollWidth - containerWidth;
              const scrollTarget = Math.min(maxScroll, scrollLeft + (containerWidth * 0.3));
              container.scrollTo({
                left: scrollTarget,
                behavior: 'smooth'
              });
            }
          } else {
            if (tabLeft < scrollLeft) {
              container.scrollTo({
                left: tabLeft - 10,
                behavior: 'smooth'
              });
            } else if (tabLeft + tabWidth > scrollLeft + containerWidth) {
              container.scrollTo({
                left: tabLeft - containerWidth + tabWidth + 10,
                behavior: 'smooth'
              });
            }
          }
        }
        
        this.checkArrowVisibility();
      });
    }
  }
};
</script>

<style scoped>
.tabs-scroller-container {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  transition: all 0.3s ease;
}

.tabs-scroller-container:hover {
  z-index: 20;
}

.scroll-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 40px;
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
  flex-shrink: 0;
  opacity: 0;
  transform: scale(0.9);
}

.tabs-scroller-container:hover .scroll-arrow {
  opacity: 1;
  transform: scale(1);
}

.scroll-arrow:hover {
  background: #f5f7fa;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transform: translateY(-1px) scale(1.05);
}

.scroll-arrow.hidden {
  opacity: 0 !important;
  pointer-events: none;
  transform: scale(0.8);
}

.scroll-arrow i {
  font-size: 18px;
  color: #606266;
  font-weight: bold;
  transition: color 0.2s ease;
}

.scroll-arrow:hover i {
  color: var(--tech-primary);
}

.tabs-container {
  flex: 1;
  overflow-x: auto;
  white-space: nowrap;
  -ms-overflow-style: none;
  scrollbar-width: none;
  display: flex;
  padding: 8px 0;
  scroll-behavior: smooth;
  transition: padding 0.3s ease;
}

.tabs-container::-webkit-scrollbar {
  display: none;
}

.tab-item {
  position: relative;
  padding: 12px 24px;
  margin: 0 8px;
  cursor: pointer;
  font-size: 17px;
  color: #606266;
  transition: all 0.3s ease;
  flex-shrink: 0;
  border-radius: 6px;
  /* background: #ecf5ff; */
}

.tab-item:first-child {
  margin-left: 0;
}

.tab-item:last-child {
  margin-right: 0;
}

.tab-item:hover {
  color: var(--tech-primary);
  /* background: #ecf5ff; */
}

.tab-item.active {
  color: var(--tech-primary);
  font-weight: 600;
  /* background: #ecf5ff; */
}

.tab-indicator {
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 4px;
  background: linear-gradient(90deg, var(--tech-primary), #79bbff);
  border-radius: 3px;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.4);
  animation: indicatorAppear 0.3s ease;
}

@keyframes indicatorAppear {
  from {
    opacity: 0;
    transform: translateX(-50%) scaleX(0.5);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) scaleX(1);
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .scroll-arrow {
    width: 28px;
    height: 36px;
  }
  
  .scroll-arrow i {
    font-size: 16px;
  }
  
  .tab-item {
    padding: 10px 16px;
    font-size: 14px;
    margin: 0 6px;
  }
  
  .tabs-container {
    padding: 6px 0;
  }
}
</style>